import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import SVG from 'react-inlinesvg';
import cx from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { isEqual } from 'lodash';
import plusSvg from 'assets/icons/plus.svg';
import locationSvg from 'assets/icons/location.svg';
import formatsSvg from 'assets/icons/formats.svg';
import networksSvg from 'assets/icons/networks.svg';
import frameListSvg from 'assets/icons/frame.svg';
import { FormatLabel } from 'store/userOptions/reducer.types';
import Search from 'lib/Search';
import { Store } from 'components/common/types/Store.types';
import type { CodeNameModel } from 'components/common/types';
import type { Option } from 'components/common/types/DropdownItem.types';
import DropdownItem from 'lib/MultiLevelDropdown/DropdownItem';
import { transformLocationData } from 'components/common/FilterArea/Filters/FilterLocation/locationUtils';
import { useOutsideClick } from 'customHooks/useOutsideClick';
import Button, { Color, Size } from 'lib/Button';
import { RouteFrameCode } from 'components/common/types/Deal.types';
import { FileList } from 'components/common/types';
import { sortByName } from 'utils/sortByName';
import { AddFilterProps, FormatKey, Group, LocationItem, FormatItem } from './AddFilter.types';
import { transformNetworkData } from './networkUtils';
import { updateSelectedNetworks } from './networkFilterUtils';
import FrameListPanel from './FrameListPanel/FrameListPanel';

const getFilteredOptions = (options: Option[], searchLower: string): Option[] => {
  if (!searchLower) return options;

  return options.reduce<Option[]>((acc, option) => {
    const { name, children } = option;
    if (name.toLowerCase().includes(searchLower)) {
      acc.push(option);
      return acc;
    }

    if (children && children.length) {
      const kids = getFilteredOptions(children, searchLower);
      if (kids.length) acc.push({ ...option, children: kids });
    }

    return acc;
  }, []);
};

const filterOptions = (options: Option[], search: string): Option[] => {
  if (!search || search.length < 3) return options;

  return getFilteredOptions(options, search.toLowerCase());
};

const AddFilter: React.FC<AddFilterProps> = ({
  selectedLocations = [],
  selectedFormats = [],
  selectedNetworks = [],
  selectedFrameList,
  changeFilters,
  isDisabled = false,
  isScrollEnabled = true,
  dataTestId,
}) => {
  const [open, setOpen] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState<string | number | null>(null);

  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [selectedRouteFrameCodes, setSelectedRouteFrameCodes] = useState<RouteFrameCode[]>(
    selectedFrameList?.routeFrameCodes || [],
  );
  const [uploadedFrameLists, setUploadedFrameLists] = useState<FileList[]>(selectedFrameList?.uploadedFrameLists || []);

  const dropdownRef = useRef() as React.MutableRefObject<HTMLDivElement>;
  const id = useRef(uuidv4());

  useEffect(() => {
    if (selectedFrameList) {
      if (!isEqual(selectedFrameList.routeFrameCodes, selectedRouteFrameCodes)) {
        setSelectedRouteFrameCodes(selectedFrameList.routeFrameCodes || []);
      }
      if (!isEqual(selectedFrameList.uploadedFrameLists, uploadedFrameLists)) {
        setUploadedFrameLists(selectedFrameList.uploadedFrameLists || []);
      }
    }
  }, [selectedFrameList]);

  useOutsideClick(dropdownRef, () => {
    setOpen(false);
    setActiveKey(null);
    setSearch('');
  });

  const referenceLocationData = useSelector((store: Store) => store.userOptions.referenceOptions.location);
  const referenceNetworkData = useSelector((store: Store) => store.userOptions.networks);
  const isLoadingRefs = useSelector((store: Store) => store.dealManagement.isFetchingReferences);
  const availableFormats = useSelector(
    (store: Store) => store.userOptions.referenceOptions.format as unknown as Record<string, CodeNameModel[]>,
  );

  const locationOptions: Option[] = useMemo(() => {
    if (isLoadingRefs || !referenceLocationData) return [];
    return transformLocationData(referenceLocationData);
  }, [referenceLocationData, isLoadingRefs]);

  const formatOptions: Option[] = useMemo(() => {
    const entries = Object.entries(availableFormats) as [FormatKey, CodeNameModel[]][];
    return sortByName(
      entries.reduce<Option[]>((acc, [groupKey, items]) => {
        if (items.length > 0) {
          acc.push({
            code: groupKey,
            name: FormatLabel[groupKey],
            selectable: false,
            children: sortByName(
              items.map(({ code, name }) => ({
                code,
                name,
                selectable: true,
                parentCode: groupKey,
              })),
            ),
          });
        }
        return acc;
      }, []),
    );
  }, [availableFormats]);

  const networkOptions: Option[] = useMemo(() => {
    if (isLoadingRefs || !referenceNetworkData) return [];
    return transformNetworkData(referenceNetworkData);
  }, [referenceNetworkData, isLoadingRefs]);

  const onChangeLocation = (node: Option, checked: boolean): void => {
    let parentName: string | undefined;
    if (node.parentCode) {
      const parentOption = locationOptions.find((opt) => opt.code === node.parentCode);
      parentName = parentOption?.name;
    }

    const locationItem: LocationItem = {
      code: String(node.code),
      name: node.name,
      parentCode: node.parentCode ? String(node.parentCode) : undefined,
      parentName,
    };

    const newLocations = checked
      ? [...selectedLocations, locationItem]
      : selectedLocations.filter((location) => location.code !== locationItem.code);

    changeFilters({ locations: newLocations });
  };

  const onChangeFormat = (node: Option, checked: boolean): void => {
    const formatItem: FormatItem = {
      code: String(node.code),
      name: node.name,
      parentCode: String(node.parentCode),
      parentName: FormatLabel[node.parentCode as FormatKey] || String(node.parentCode),
    };

    const newFormats = checked
      ? [...selectedFormats, formatItem]
      : selectedFormats.filter((format) => format.code !== formatItem.code);

    changeFilters({ formats: newFormats });
  };

  const onChangeNetwork = (node: Option, checked: boolean): void => {
    const newNetworks = updateSelectedNetworks(node, checked, selectedNetworks, networkOptions);
    changeFilters({ networks: newNetworks });
  };

  const selectedLocationCodes = useMemo(() => {
    return selectedLocations.map((location) => location.code);
  }, [selectedLocations]);

  const selectedFormatCodes = useMemo(() => {
    return selectedFormats.map((format) => format.code);
  }, [selectedFormats]);

  const selectedNetworkCodes = useMemo(() => {
    return selectedNetworks.map((network) => network.code);
  }, [selectedNetworks]);

  const handleFrameListChange = (): void => {
    if (
      !isEqual(selectedRouteFrameCodes, selectedFrameList?.routeFrameCodes) ||
      !isEqual(uploadedFrameLists, selectedFrameList?.uploadedFrameLists)
    ) {
      changeFilters({
        frameList: {
          routeFrameCodes: selectedRouteFrameCodes,
          uploadedFrameLists,
        },
      });
    }
  };

  useEffect(() => {
    if (activeKey === 'frameList') {
      handleFrameListChange();
    }
  }, [selectedRouteFrameCodes, uploadedFrameLists, activeKey]);

  const groups: Group[] = useMemo(() => {
    return [
      {
        key: 'location',
        label: 'Location',
        icon: locationSvg,
        options: locationOptions,
        selected: selectedLocations,
        selectedCodes: selectedLocationCodes,
        onChange: onChangeLocation,
      },
      {
        key: 'format',
        label: 'Format',
        icon: formatsSvg,
        options: formatOptions,
        selected: selectedFormats,
        selectedCodes: selectedFormatCodes,
        onChange: onChangeFormat,
      },
      {
        key: 'networks',
        label: 'Networks',
        icon: networksSvg,
        options: networkOptions,
        selected: selectedNetworks,
        selectedCodes: selectedNetworkCodes,
        onChange: onChangeNetwork,
      },
      {
        key: 'frameList',
        label: 'Frame list',
        icon: frameListSvg,
        selected: [],
        selectedCodes: [],
        isCustomPanel: true,
      },
    ];
  }, [
    locationOptions,
    selectedLocations,
    formatOptions,
    selectedFormats,
    selectedLocationCodes,
    selectedFormatCodes,
    selectedNetworks,
    selectedNetworkCodes,
    networkOptions,
  ]);

  const active = groups.find((group) => group.key === activeKey) ?? null;
  const filtered = filterOptions(active?.options ?? [], search);

  const activate = (key: string): void => {
    setActiveKey(key);
    setSearch('');
    setOpenSubMenu(null);
  };

  const renderActivePanel = (): JSX.Element | null => {
    if (!active) return null;

    if (active.isCustomPanel && active.key === 'frameList') {
      return (
        <FrameListPanel
          selectedRouteFrameCodes={selectedRouteFrameCodes}
          setSelectedRouteFrameCodes={setSelectedRouteFrameCodes}
          uploadedListOfFiles={uploadedFrameLists}
          setUploadedListOfFiles={setUploadedFrameLists}
          onRemoveFile={(fileName: string) => {
            setUploadedFrameLists((prevFiles) => prevFiles.filter((file) => file.name !== fileName));
          }}
        />
      );
    }

    return (
      <div className="min-w-62.5 bg-white border border-neutral-200 rounded-md overflow-hidden shadow-dropdownMenu">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-neutral-200">
          <div className="flex-1 min-w-0">
            <Search
              value={search}
              placeholder="Search"
              onChange={(value) => {
                setSearch(String(value ?? ''));
                setOpenSubMenu(null);
              }}
            />
          </div>
        </div>
        {isLoadingRefs && <p className="text-center px-5 py-2.5 sub-header-base">Loading…</p>}
        {!isLoadingRefs && filtered.length ? (
          <ul
            className={cx('py-3', { 'max-h-72 overflow-y-auto overflow-x-hidden': isScrollEnabled })}
            aria-label={`${id.current}-options-list`}
            onMouseLeave={() => setOpenSubMenu(null)}
          >
            {filtered.map((option) => (
              <DropdownItem
                key={`${option.code}-${option.name}`}
                multiLevelDropdownId={id.current}
                option={option}
                setIsSubMenuOpen={(code) => setOpenSubMenu(code)}
                isSubMenuOpen={openSubMenu === option.code}
                isSelectable={option.selectable}
                selectedItems={active.selectedCodes}
                onChangeSelectedItem={active.onChange}
                isPositionFixed={false}
                depth={isScrollEnabled ? 0 : 1}
              />
            ))}
          </ul>
        ) : (
          <p className="text-center px-5 py-2.5 sub-header-base">No results found</p>
        )}
      </div>
    );
  };

  return (
    <div className="relative" ref={dropdownRef} data-test-id={dataTestId}>
      <div className="h-[38px] flex">
        <Button
          svg={plusSvg}
          label="Add filter"
          onClick={() => setOpen((value) => !value)}
          isDisabled={isDisabled}
          size={Size.FIT_CONTAINER}
          color={Color.BLACK_AND_WHITE}
        />
      </div>
      {open && (
        <div className="absolute z-50 mt-1 bg-transparent" onMouseLeave={() => setActiveKey(null)}>
          <div className="flex items-start gap-1 ">
            <div className="w-52 bg-white border border-neutral-200 rounded-md overflow-hidden shadow-dropdownMenu">
              <div className={cx({ 'max-h-72 overflow-y-auto': groups.length > 6 })}>
                {groups.map((group) => (
                  <button
                    key={group.key}
                    type="button"
                    className={cx(
                      'w-full px-4 py-3 flex items-center gap-2 text-left rounded-md hover:bg-neutral-200 hover:text-primary-950',
                      {
                        'bg-neutral-200 text-primary-950': activeKey === group.key,
                      },
                    )}
                    onMouseEnter={() => activate(group.key)}
                    onFocus={() => activate(group.key)}
                  >
                    <SVG className="w-4 h-4 text-neutral-700" src={group.icon} />
                    <span>{group.label}</span>
                  </button>
                ))}
              </div>
            </div>
            {renderActivePanel()}
          </div>
        </div>
      )}
    </div>
  );
};

export default AddFilter;
